import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { calculateTotal, matchTravelCost, isLocalCity } from "../../../lib/pricing";
import { api } from "../../../lib/api";
import { useAlbumConfigurator } from "../../../hooks/useAlbumConfigurator";
import { STEPS } from "./constants";

/**
 * Encapsulates all state and business logic of the quote calculator:
 * fetching packages/travel costs, form state, package/extra selection,
 * total calculation and quote submission.
 */
export const useQuoteCalculation = (prices, presetCategory) => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [packages, setPackages] = useState([]);
  const [travelCosts, setTravelCosts] = useState([]);
  const [extras, setExtras] = useState([]);
  const [album, setAlbumState] = useState(null);
  const [activeCategory, setActiveCategory] = useState("bodas");
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", event_date: "", city: "", extras: [] });
  const hasTrackedConversion = useRef(false);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data));
    api.get("/travel-costs").then((r) => setTravelCosts(r.data));
    api.get("/extras").then((r) => setExtras(r.data));
  }, []);

  useEffect(() => {
    if (presetCategory) {
      setActiveCategory(presetCategory);
      setStep(0);
    }
  }, [presetCategory]);

  const update = useCallback((field, value) => setForm((f) => ({ ...f, [field]: value })), []);

  const toggleExtra = useCallback((key) => {
    setForm((f) => ({
      ...f,
      extras: f.extras.includes(key) ? f.extras.filter((e) => e !== key) : [...f.extras, key],
    }));
  }, []);

  const isSelected = useCallback((pkgId) => selectedPackages.some((p) => p.id === pkgId), [selectedPackages]);

  const setAlbum = useCallback((payload, price, summary) => {
    setAlbumState(payload ? { payload, price, summary } : null);
  }, []);

  const albumConfig = useAlbumConfigurator(setAlbum);

  const togglePackage = useCallback((pkg) => {
    setSelectedPackages((sel) => (sel.some((p) => p.id === pkg.id) ? sel.filter((p) => p.id !== pkg.id) : [...sel, pkg]));
  }, []);

  const matchedTravelCost = isLocalCity(form.city) ? null : matchTravelCost(form.city, travelCosts);
  const total = calculateTotal(selectedPackages, form.extras, extras) + (matchedTravelCost?.price || 0) + (album?.price || 0);
  const categoryPackages = packages.filter((p) => p.category === activeCategory);

  const canNext = useCallback(() => {
    if (step === 0) return form.name.trim() && form.phone.trim();
    if (step === 1) return form.event_date && form.city.trim();
    if (step === 2) return selectedPackages.length > 0 || !!album;
    return true;
  }, [step, form, selectedPackages, album]);

  const next = useCallback(() => setStep((s) => (s < STEPS.length - 1 ? s + 1 : s)), []);
  const prev = useCallback(() => setStep((s) => (s > 0 ? s - 1 : s)), []);

  const submitQuote = useCallback(async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/quotes", {
        ...form, package_ids: selectedPackages.map((p) => p.id), album: album?.payload || null,
      });
      setResult(data);
      if (!hasTrackedConversion.current && typeof window.gtag === "function") {
        window.gtag("event", "conversion", { send_to: "AW-18275777371/ECpqCIPEyNccENv2yIpE" });
      }
      if (!hasTrackedConversion.current && typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }
      hasTrackedConversion.current = true;
    } catch (e) {
      toast.error("No se pudo generar la cotización. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  }, [form, selectedPackages, album]);

  useEffect(() => {
    if (step === STEPS.length - 1 && !result && !submitting) submitQuote();
  }, [step, result, submitting, submitQuote]);

  return {
    step, next, prev, canNext,
    activeCategory, setActiveCategory, categoryPackages,
    selectedPackages, isSelected, togglePackage,
    form, update, toggleExtra, extras,
    album, albumConfig,
    matchedTravelCost, total,
    submitting, result,
  };
};
